import debounce from 'lodash/debounce'
import Vue from 'vue'
import Router from 'vue-router'

import constantRoutes from './routes/constant-routes'

import Config from '@/config'
import TrackingEvent from '@/plugins/tracking/event'
import locator from '@/service-locator'
import adsRouteGuard from '@/views/ads/permission'
import farahRouteGuard from '@/views/farah/permission'

/* Router Modules */
import { GUIDESAIL_ID } from '@/constants/guidesail-config'
import Layout from '@/layout/index.vue'
import {
  canUserAccessApiViewByMerchantStatusLegacy,
  canUserAccessApiViewByMerchantType,
} from '@/utils/api-token'
import { BUSINESS_LENDING_SIDEBAR_FEATURE_FLAG } from '@/constants/business-lending'
import { getABTProvider } from '@/plugins/abt/statsig'
import {
  getOrderListPath,
  isMerchantOrdersListV2Enabled,
  redirectToOrderListPath,
  redirectToOrderDetailPath,
} from '@/utils/order-routes'
import {
  isMerchantBusinessAddressV2Enabled,
  redirectToAddressCollectionPath,
  ADDRESS_COLLECTION_PATH_V1,
  ADDRESS_COLLECTION_PATH_V2,
} from '@/utils/address-collection-routes'
import {
  isMerchantUserSettingsV2Enabled,
  redirectToUserSettingsPath,
  USER_SETTINGS_PATH_V2,
} from '@/utils/user-settings-routes'
import {
  isMerchantExportedRequestsV2Enabled,
  redirectToExportedListPath,
  EXPORTED_LIST_PATH_V1,
  EXPORTED_LIST_PATH_V2,
} from '@/utils/export-routes'
import AdsPostpaidResponse from '@/views/ads/ads-postpaid-response/index'
import AdsTopUpResponse from '@/views/ads/ads-top-up-response/index'
import { CREATE_EDIT_ADS } from '@/views/ads/constants/ads-permissions'
import { getToken } from '@/utils/auth'
import { redirectToOnboardingV3 } from '@/utils/onboarding-session'

Vue.use(Router)

const orderListRouteMeta = {
  title: 'tab_order_list',
  label: 'tab_order_list',
  affix: true,
  breadcrumb: true,
  permissions: ['order_view_list_v2'],
  productTour: {
    idEn: GUIDESAIL_ID.order_list.en,
    idAr: GUIDESAIL_ID.order_list.ar,
  },
  pageEvent: TrackingEvent.page_view.order_list_viewed,
  trackEvent: TrackingEvent.global_navbar_orders_clicked,
}

const isBusinessLendingSidebarDisabled = () => {
  const abtProvider = getABTProvider()

  return !abtProvider.isFeatureEnabled(BUSINESS_LENDING_SIDEBAR_FEATURE_FLAG)
}

/** Path/component are chosen once when routes load, based on the feature flag at that moment. */
function createOrderListRoute() {
  const isV2 = isMerchantOrdersListV2Enabled()

  return {
    path: isV2 ? '/v2/orders' : '/order/list',
    component: isV2 ? () => {} : () => import('@/views/order/list'),
    name: 'order-list',
    beforeEnter: isV2 ? (to) => redirectToOrderListPath(to) : undefined,
    meta: {
      ...orderListRouteMeta,
      ...(isV2 ? { useFullPath: true } : {}),
    },
  }
}

/** Path/component are chosen once when routes load, based on the feature flag at that moment. */
function createAddressCollectionRoute() {
  const isV2 = isMerchantBusinessAddressV2Enabled()

  return {
    path: isV2 ? ADDRESS_COLLECTION_PATH_V2 : ADDRESS_COLLECTION_PATH_V1,
    component: isV2
      ? () => {}
      : () => import('@/views/users/address-collection'),
    name: 'address-collection-view',
    beforeEnter: isV2 ? (to) => redirectToAddressCollectionPath(to) : undefined,
    meta: {
      title: 'address-collection',
      label: 'address-collection',
      affix: true,
      breadcrumb: true,
      permissions: ['get_merchant_address_list'],
      trackEvent: TrackingEvent.global_navbar_business_address_clicked,
      ...(isV2 ? { useFullPath: true } : {}),
    },
  }
}

/** Path/component are chosen once when routes load, based on the feature flag at that moment. */
function createExportedListRoute() {
  const isV2 = isMerchantExportedRequestsV2Enabled()

  return {
    path: isV2 ? EXPORTED_LIST_PATH_V2 : EXPORTED_LIST_PATH_V1,
    component: isV2 ? () => {} : () => import('@/views/exports/list'),
    name: 'exports-list',
    beforeEnter: isV2 ? (to) => redirectToExportedListPath(to) : undefined,
    meta: {
      title: 'header_exports',
      label: 'header_exports',
      affix: true,
      breadcrumb: true,
      permissions: ['get_exported_reports'],
      productTour: {
        idEn: GUIDESAIL_ID.export_list.en,
        idAr: GUIDESAIL_ID.export_list.ar,
      },
      trackEvent: TrackingEvent.global_navbar_exported_requests_clicked,
      ...(isV2 ? { useFullPath: true } : {}),
    },
  }
}

function createOrderDetailRoute() {
  const isV2 = isMerchantOrdersListV2Enabled()

  return {
    path: isV2 ? '/v2/orders/detail/:orderId' : '/order/detail/:orderId',
    component: isV2 ? () => {} : () => import('@/views/order/detail'),
    name: 'order-detail',
    beforeEnter: isV2 ? (to) => redirectToOrderDetailPath(to) : undefined,
    meta: {
      title: 'header_order_detail',
      label: 'header_order_detail',
      affix: false,
      breadcrumb: true,
      permissions: ['order_view_list_v2', 'order_view_detail'],
      productTour: {
        idEn: GUIDESAIL_ID.order_detail.en,
        idAr: GUIDESAIL_ID.order_detail.ar,
      },
      ...(isV2 ? { useFullPath: true } : {}),
    },
    hidden: true,
  }
}

/** Path/component are chosen once when routes load, based on the feature flag at that moment. */
function createUserSettingsRoute() {
  const isV2 = isMerchantUserSettingsV2Enabled()

  if (isV2) {
    return {
      path: USER_SETTINGS_PATH_V2,
      component: () => {},
      name: 'profile',
      beforeEnter: (to) => redirectToUserSettingsPath(to),
      meta: {
        title: 'header_profile',
        label: 'header_profile',
        icon: 'profile',
        iconActive: 'profile',
        breadcrumb: false,
        useFullPath: true,
      },
      hidden: true,
    }
  }

  return {
    path: '/profile',
    component: Layout,
    redirect: '/profile/index',
    meta: {
      title: 'header_profile',
      label: 'header_profile',
      icon: 'profile',
      iconActive: 'profile',
      breadcrumb: false,
    },
    hidden: true,
    children: [
      {
        path: 'index',
        component: () => import('@/views/profile'),
        name: 'profile',
        meta: {
          title: 'header_profile',
          label: 'header_profile',
          icon: '',
          affix: true,
          breadcrumb: true,
        },
      },
    ],
  }
}

/**
 * Note: sub-menu only appear when route children.length >= 1
 * Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
 *
 * hidden: true                   if set true, item will not show in the sidebar(default is false)
 * redirect: noRedirect           if set noRedirect will not redirect in the breadcrumb
 * name:'router-name'             the name is used by <keep-alive> (must set!!!)
 * meta : {
 permissions: ['view_order_list','edit_order']    control the page roles (you can set multiple permissions)
 title: 'title'               the name show in sidebar and breadcrumb (recommend set)
 icon: 'svg-name'             the icon show in the sidebar
 noCache: true                if set true, the page will no be cached(default is false)
 affix: true                  if set true, the tag will affix in the tags-view
 breadcrumb: false            if set false, the item will hidden in breadcrumb(default is true)
 activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
 }
 */

/**
 * asyncRoutes
 * the routes that need to be dynamically loaded based on user permissions
 */
export const asyncRoutes = [
  {
    path: '/dashboard',
    component: Layout,
    name: 'dashboard',
    meta: {
      title: 'Dashboard',
      label: 'Dashboard',
      icon: 'dashboard',
      iconActive: 'dashboard',
      breadcrumb: true,
      permissions: ['view_dashboard_overview'],
    },
    children: [
      {
        path: '/dashboard/home',
        component: () => import('@/views/dashboard'),
        name: 'dashboard-home',
        meta: {
          title: 'tab_dashboard_list_home',
          label: 'tab_dashboard_list_home',
          icon: 'dashboard',
          affix: true,
          breadcrumb: true,
          permissions: ['view_dashboard_overview'],
          productTour: {
            idEn: GUIDESAIL_ID.dashboard.en,
            idAr: GUIDESAIL_ID.dashboard.ar,
          },
          pageEvent: TrackingEvent.page_view.dashboard_viewed,
          trackEvent: TrackingEvent.global_navbar_dashboard_clicked,
        },
      },
    ],
  },
  {
    path: '/orders',
    component: Layout,
    name: 'order',
    redirect: () => getOrderListPath(),
    meta: {
      title: 'header_orders',
      label: 'header_orders',
      icon: 'orders',
      iconActive: 'orders',
      breadcrumb: true,
      permissions: ['order_view_list_v2'],
      highlight: true,
    },
    children: [
      createOrderListRoute(),
      createOrderDetailRoute(),
      {
        path: '/orders/new',
        component: () => import('@/views/order/create'),
        meta: {
          title: 'header_order_create',
          label: 'header_order_create',
          affix: false,
          breadcrumb: true,
          permissions: ['order_create'],
        },
        hidden: true,
      },
    ],
  },
  {
    path: '/dispute',
    component: Layout,
    name: 'dispute',
    redirect: '/dispute/list',
    meta: {
      title: 'header_disputes',
      label: 'header_disputes',
      icon: 'disputes',
      iconActive: 'disputes',
      breadcrumb: true,
      permissions: ['get_dispute_listing'],
      highlight: true,
    },
    children: [
      {
        path: '/dispute/list',
        component: () => import('@/views/dispute/list'),
        name: 'dispute-list',
        meta: {
          title: 'header_disputes',
          label: 'header_disputes',
          icon: 'disputes',
          iconActive: 'disputes',
          affix: true,
          breadcrumb: true,
          permissions: ['get_dispute_listing'],
          productTour: {
            idEn: GUIDESAIL_ID.dispute_list.en,
            idAr: GUIDESAIL_ID.dispute_list.ar,
          },
          pageEvent: TrackingEvent.page_view.dispute_list_viewed,
          trackEvent: TrackingEvent.global_navbar_disputes_clicked,
        },
      },
      {
        path: '/dispute/detail/:disputeId',
        component: () => import('@/views/dispute/detail'),
        name: 'dispute-detail',
        meta: {
          title: 'header_dispute_detail',
          label: 'header_dispute_detail',
          affix: false,
          breadcrumb: true,
          permissions: ['get_dispute_listing', 'get_dispute_details'],
          productTour: {
            idEn: GUIDESAIL_ID.dispute_detail.en,
            idAr: GUIDESAIL_ID.dispute_detail.ar,
          },
        },
        hidden: true,
      },
    ],
  },
  {
    path: '/transaction',
    component: Layout,
    name: 'transactional',
    redirect: '/transaction/list',
    meta: {
      title: 'header_transactional',
      label: 'header_transactional',
      icon: 'transactions',
      iconActive: 'transactions',
      breadcrumb: true,
      permissions: ['view_transaction'],
    },
    children: [
      {
        path: '/transaction/list',
        component: () => import('@/views/transactional/list'),
        name: 'transactional-list',
        meta: {
          title: 'header_transactional',
          label: 'header_transactional',
          affix: true,
          breadcrumb: true,
          permissions: ['view_transaction'],
          pageEvent: TrackingEvent.page_view.transaction_list_viewed,
          trackEvent: TrackingEvent.global_navbar_transactions_clicked,
        },
      },
    ],
  },
  {
    path: '/capital',
    component: Layout,
    name: 'capital',
    meta: {
      title: 'tab_capital',
      label: 'tab_capital',
      icon: 'capital',
      iconActive: 'capital',
      breadcrumb: false,
      useFullPath: true,
    },
    disabled: isBusinessLendingSidebarDisabled,
    hidden: isBusinessLendingSidebarDisabled,
  },
  {
    path: '/banking/dashboard',
    component: Layout,
    name: 'banking-dashboard',
    meta: {
      title: 'tab_banking_account',
      label: 'tab_banking_account',
      icon: 'business-banking-account',
      iconActive: 'business-banking-account',
      breadcrumb: false,
      useFullPath: true,
    },
    disabled: isBusinessLendingSidebarDisabled,
    hidden: isBusinessLendingSidebarDisabled,
  },
  {
    path: '/banking/transactions',
    component: Layout,
    name: 'banking-transactions',
    meta: {
      title: 'tab_banking_transactions',
      label: 'tab_banking_transactions',
      icon: 'business-banking-transactions',
      iconActive: 'business-banking-transactions',
      breadcrumb: false,
      useFullPath: true,
    },
    disabled: isBusinessLendingSidebarDisabled,
    hidden: isBusinessLendingSidebarDisabled,
  },
  {
    path: '/banking/cards',
    component: Layout,
    name: 'banking-cards',
    meta: {
      title: 'tab_banking_cards',
      label: 'tab_banking_cards',
      icon: 'business-banking-cards',
      iconActive: 'business-banking-cards',
      breadcrumb: false,
      useFullPath: true,
    },
    disabled: isBusinessLendingSidebarDisabled,
    hidden: isBusinessLendingSidebarDisabled,
  },
  {
    path: '/ads',
    component: Layout,
    name: 'ads',
    redirect: '/ads/dashboard',
    disabled: !Config.isEnabledAdtechAds,
    beforeEnter: adsRouteGuard,
    meta: {
      title: 'ads_header',
      label: 'ads_header',
      icon: 'tamara-ads',
      iconActive: 'tamara-ads',
      breadcrumb: true,
      //! Temporary for testing, remove later
      permissions: ['access_adtech'],
    },
    children: [
      {
        path: '/ads/campaign-insights/:campaignId',
        component: () => import('@/views/ads/campaign-insights'),
        name: 'audience-insights',
        meta: {
          title: 'ads_header',
          label: 'ads_header',
        },
        hidden: true,
      },
      {
        path: '/ads/dashboard',
        component: () => import('@/views/ads/dashboard'),
        name: 'ads-dashboard',
        meta: {
          title: 'ads_manager',
          label: 'ads_manager',
        },
      },
      {
        path: '/ads/create',
        component: () => import('@/views/ads/create'),
        name: 'ads-create',
        meta: {
          title: 'ads_create_new_campaign',
          label: 'ads_header',
        },
        hidden: true,
      },
      {
        path: '/ads/ads-credit/top-up',
        component: () => import('@/views/ads/ads-top-up'),
        name: 'ads-top-up',
        meta: {
          title: 'ads_top_up_nav_label',
          label: 'ads_top_up_nav_label',
        },
        hidden: true,
      },
      {
        path: '/ads/ads-credit',
        component: () => import('@/views/ads/ads-credit'),
        name: 'ads-credit',
        meta: {
          title: 'ads_billing',
          label: 'ads_billing',
        },
      },
      {
        path: '/ads/detail/:campaignId',
        component: () => import('@/views/ads/detail'),
        name: 'ads-detail',
        meta: {
          title: 'ads_header',
          label: 'ads_header',
          affix: true,
          breadcrumb: true,
        },
        hidden: true,
      },
      {
        path: '/ads/postpaid/invoice/settings',
        component: () => import('@/views/ads/postpaid-invoice-settings'),
        name: 'ads-postpaid-invoice-settings',
        meta: {
          title: 'ads_postpaid_invoice_settings',
          label: 'ads_postpaid_invoice_settings',
        },
        hidden: true,
      },
      {
        path: '/ads/postpaid/invoice',
        component: () => import('@/views/ads/postpaid-invoice'),
        name: 'ads-postpaid-invoice',
        meta: {
          title: 'ads_postpaid_invoice',
          label: 'ads_postpaid_invoice',
        },
        hidden: true,
      },
      {
        path: '/ads/postpaid/invoice/:invoiceId',
        component: () => import('@/views/ads/postpaid-invoice-details'),
        name: 'ads-postpaid-invoice-details',
        meta: {
          title: 'ads_postpaid_invoice',
          label: 'ads_postpaid_invoice',
        },
        hidden: true,
      },
      {
        path: '/ads/activity-logs',
        component: () => import('@/views/ads/activity-logs'),
        name: 'activity-logs',
        meta: {
          title: 'ads_activity_logs',
          label: 'ads_activity_logs',
          permissions: [CREATE_EDIT_ADS],
        },
      },
      {
        path: '/ads/conversion-orders/:campaignId',
        component: () => import('@/views/ads/conversion-orders'),
        name: 'ads-conversion-orders',
        meta: {
          title: 'ads_sales_conversion_reports',
          label: 'ads_sales_conversion_reports',
        },
        hidden: true,
      },
    ],
  },
  {
    path: '/deals',
    component: Layout,
    name: 'deals',
    redirect: '/deals/dashboard',
    beforeEnter: farahRouteGuard,
    meta: {
      title: 'farah_tamara_deals',
      label: 'farah_tamara_deals',
      icon: 'farah',
      iconActive: 'farah',
      breadcrumb: true,
      //! Temporary for testing, remove later
      permissions: ['access_adtech'],
    },
    children: [
      {
        path: '/deals/dashboard',
        component: () => import('@/views/farah/dashboard'),
        name: 'deals-dashboard',
        meta: {
          title: 'farah_deals_manager',
          label: 'farah_deals_manager',
        },
      },
      {
        path: '/deals/billing',
        component: () => import('@/views/farah/farah-credit'),
        name: 'deals-billing',
        meta: {
          title: 'deals_billing',
          label: 'deals_billing',
          trackEvent: TrackingEvent.global_navbar_deals_billing_clicked,
        },
        hidden: !Config.isEnabledDealsBilling,
      },
      {
        path: '/deals/create',
        component: () => import('@/views/farah/create'),
        name: 'deals-create',
        meta: {
          title: 'farah_create_a_deal',
          label: 'farah_create_a_deal',
        },
        hidden: true,
      },
      {
        path: '/deals/edit/:promotionId',
        component: () => import('@/views/farah/detail'),
        name: 'deals-edit',
        meta: {
          title: 'farah_edit_a_campagin',
          label: 'farah_edit_a_campagin',
          affix: true,
          breadcrumb: true,
        },
        hidden: true,
      },
      {
        path: '/deals/insights/:promotionId',
        component: () => import('@/views/farah/promotion-insights'),
        name: 'deals-insights',
        meta: {
          title: 'farah_insights',
          label: 'farah_insights',
        },
        hidden: true,
      },
      {
        path: '/deals/payment-history',
        component: () => import('@/views/farah/farah-pament-history'),
        name: 'deals-payment-history',
        meta: {
          title: 'farah_payment_history',
          label: 'farah_payment_history',
        },
        hidden: true,
      },
      {
        path: '/deals/billing-detail/:id',
        component: () => import('@/views/farah/farah-credit/detail'),
        name: 'deals-billing-detail',
        meta: {
          title: 'farah_deals_billing_details',
          label: 'farah_deals_billing_details',
        },
        hidden: true,
      },
      {
        path: '/deals/billing-detail/:id/pay',
        component: () => import('@/views/farah/farah-credit/pay'),
        name: 'deals-billing-pay',
        meta: {
          title: 'farah_deals_billing_pay',
          label: 'farah_deals_billing_pay',
        },
        hidden: true,
      },
    ],
    hidden: (store) => {
      const merchantProfile = store?.getters?.merchantProfile
      const isEnabledFarahPromotionReview = ['SA', 'AE'].includes(
        merchantProfile?.country
      )

      if (isEnabledFarahPromotionReview) {
        return false
      }

      return true
    },
  },
  {
    path: '/ads/top-up-success',
    component: AdsTopUpResponse,
    name: 'ads-top-up-success',
    disabled: !Config.isEnabledAdtechAds,
    beforeEnter: adsRouteGuard,
    meta: {
      title: 'ads_top_up_success_title',
      responseType: 'success',
      //! Temporary for testing, remove later
      permissions: ['access_adtech'],
    },
    hidden: true,
  },
  {
    path: '/ads/top-up-failed',
    component: AdsTopUpResponse,
    name: 'ads-top-up-failed',
    disabled: !Config.isEnabledAdtechAds,
    beforeEnter: adsRouteGuard,
    meta: {
      title: 'ads_top_up_failed_title',
      responseType: 'error',
      //! Temporary for testing, remove later
      permissions: ['access_adtech'],
    },
    hidden: true,
  },
  {
    path: '/ads/pay-now',
    component: AdsTopUpResponse,
    name: 'ads_top_up_pay_now',
    disabled: !Config.isEnabledAdtechAds,
    beforeEnter: adsRouteGuard,
    meta: {
      title: 'ads_top_up_pay_now',
      responseType: 'pay-now',
      //! Temporary for testing, remove later
      permissions: ['access_adtech'],
    },
    hidden: true,
  },
  {
    path: '/ads/postpaid/pay-now',
    component: AdsPostpaidResponse,
    name: 'ads-postpaid-pay-now',
    disabled: !Config.isEnabledAdtechAds,
    beforeEnter: adsRouteGuard,
    meta: {
      title: 'ads_postpaid_pay_now',
      label: 'ads_postpaid_pay_now',
      responseType: 'pay-now',
    },
    hidden: true,
  },
  {
    path: '/ads/postpaid/success',
    component: AdsPostpaidResponse,
    name: 'ads-postpaid-pay-success',
    disabled: !Config.isEnabledAdtechAds,
    beforeEnter: adsRouteGuard,
    meta: {
      title: 'ads_payment_successful',
      label: 'ads_payment_successful',
      responseType: 'success',
    },
    hidden: true,
  },
  {
    path: '/ads/postpaid/failure',
    component: AdsPostpaidResponse,
    disabled: !Config.isEnabledAdtechAds,
    name: 'ads-postpaid-pay-failure',
    beforeEnter: adsRouteGuard,
    meta: {
      title: 'ads_payment_failed',
      label: 'ads_payment_failed',
      responseType: 'error',
    },
    hidden: true,
  },
  {
    path: '/settlement',
    component: Layout,
    name: 'settlements',
    redirect: '/settlement/information',
    meta: {
      title: 'tab_settlement',
      label: 'tab_settlement',
      icon: 'settlements',
      iconActive: 'settlements',
      breadcrumb: true,
      permissions: ['settlement_view', 'invoice_view'],
    },
    children: [
      {
        path: '/settlement/information',
        component: () => import('@/views/settlement/index'),
        name: 'settlement',
        meta: {
          title: 'tab_settlement_info',
          label: 'tab_settlement_info',
          icon: 'settlements',
          iconActive: 'settlements',
          affix: true,
          permissions: ['settlement_view'],
          productTour: {
            idEn: GUIDESAIL_ID.settlement_information.en,
            idAr: GUIDESAIL_ID.settlement_information.ar,
          },
          pageEvent: TrackingEvent.page_view.settlement_list_viewed,
          trackEvent: TrackingEvent.global_navbar_settlement_clicked,
        },
      },
    ],
  },
  {
    path: '/invoices',
    component: Layout,
    name: 'invoices',
    redirect: '/invoices/list',
    meta: {
      title: 'tab_invoice_list',
      label: 'tab_invoice_list',
      icon: 'invoices',
      iconActive: 'invoices',
      breadcrumb: true,
      permissions: ['invoice_view'],
    },
    children: [
      {
        path: '/invoices/list',
        component: () => import('@/views/invoice/list'),
        name: 'invoice-list',
        meta: {
          title: 'tab_invoice_list',
          label: 'tab_invoice_list',
          icon: 'invoices',
          iconActive: 'invoices',
          affix: true,
          breadcrumb: true,
          permissions: ['invoice_view'],
          productTour: {
            idEn: GUIDESAIL_ID.invoice_list.en,
            idAr: GUIDESAIL_ID.invoice_list.ar,
          },
          pageEvent: TrackingEvent.page_view.invoice_list_viewed,
          trackEvent: TrackingEvent.global_navbar_invoices_clicked,
        },
      },
    ],
  },
  {
    path: '/general-settings',
    component: Layout,
    name: 'general-settings',
    redirect: '/address-collection/view',
    meta: {
      title: 'general_settings',
      label: 'general_settings',
      icon: 'general_settings',
      iconActive: 'general_settings',
      breadcrumb: true,
      permissions: [
        'get_merchant_address_list',
        'get_exported_reports',
        'get_merchant_contacts',
        'view_webhook',
        'create_webhook',
        'edit_webhook',
        'generate_api_key',
      ],
    },
    children: [
      createAddressCollectionRoute(),
      createExportedListRoute(),
      {
        path: '/contact-details/view',
        component: () => import('@/views/contact-details/index'),
        name: 'contact-details-view',
        meta: {
          title: 'contact-details',
          label: 'contact-details',
          affix: true,
          breadcrumb: true,
          permissions: ['get_merchant_contacts'],
          trackEvent: TrackingEvent.global_navbar_contact_information_clicked,
        },
      },
      {
        path: '/webhooks/list',
        component: () => import('@/views/webhooks/list'),
        name: 'webhooks-list',
        meta: {
          title: 'tab_webhooks_list',
          label: 'tab_webhooks_list',
          affix: true,
          breadcrumb: true,
          permissions: ['view_webhook'],
          trackEvent: TrackingEvent.global_navbar_webhooks_clicked,
        },
      },
      {
        path: '/webhooks/:webhookId',
        component: () => import('@/views/webhooks/detail'),
        meta: {
          title: 'header_webhooks_detail',
          label: 'header_webhooks_detail',
          affix: false,
          breadcrumb: true,
          permissions: ['view_webhook', 'create_webhook', 'edit_webhook'],
        },
        hidden: true,
      },
      {
        path: '/api/list',
        component: () => import('@/views/api/index'),
        name: 'api-list',
        meta: {
          title: 'header_api',
          label: 'header_api',
          affix: true,
          breadcrumb: true,
          permissions: ['generate_api_key'],
          tag: {
            label: 1,
            type: 'warning',
            visible: (store) => {
              return store.getters.shouldDisplayBanner ?? false
            },
          },
          trackEvent: TrackingEvent.global_navbar_api_tokens_clicked,
        },
        hidden: (store) => {
          const merchantProfile = store?.getters?.merchantProfile

          if (!merchantProfile) {
            return true
          }

          const merchantStatus = merchantProfile?.status
          const merchantTier = store.getters['profile/merchantTier']
          const isMerchantOnline = store.getters['profile/isMerchantOnline']
          const isMerchantInStore = store.getters['profile/isMerchantInStore']

          const userCanAccessByMerchantStatus = canUserAccessApiViewByMerchantStatusLegacy(
            merchantStatus,
            merchantTier
          )

          const userCanAccessByMerchantType = canUserAccessApiViewByMerchantType(
            {
              isMerchantOnline,
              isMerchantInStore,
            },
            merchantProfile
          )

          const userCanAccessApiView =
            userCanAccessByMerchantStatus && userCanAccessByMerchantType

          // If user cannot access the API view, then hide the API view
          return !userCanAccessApiView
        },
      },
      {
        path: 'docs_url',
        component: () => {},
        name: 'docs',
        meta: {
          title: 'tab_docs',
          label: 'tab_docs',
          affix: true,
          isExternal: true,
          trackEvent: TrackingEvent.global_navbar_documenation_clicked,
        },
        hidden: false,
      },
      {
        path: 'merchant_kit_url',
        component: () => {},
        name: 'kit',
        meta: {
          title: 'merchant_kit_tab',
          label: 'merchant_kit_tab',
          affix: true,
          isExternal: true,
          trackEvent: TrackingEvent.global_navbar_partner_marketing_kit_clicked,
        },
        hidden: false,
      },
    ],
  },
  {
    path: '/settings',
    component: Layout,
    name: 'settings',
    redirect: '/settings/store/list',
    meta: {
      title: 'header_settings',
      label: 'header_settings',
      icon: 'settings',
      iconActive: 'settings',
      breadcrumb: true,
      permissions: [
        'merchant_get_barcode_setting',
        'merchant_index_store',
        'merchant_view_store',
        'merchant_create_store',
        'merchant_update_store',
        'merchant_activate_store',
        'instore_material_delivery_create',
        'instore_material_delivery_list',
        'instore_material_delivery_details',
      ],
      trackEvent: TrackingEvent.global_navbar_instore_settings_clicked,
    },
    children: [
      {
        path: '/settings/store/list',
        component: () => import('@/views/settings/store/list'),
        name: 'store-list',
        meta: {
          title: 'tab_store_list',
          label: 'tab_store_list',
          affix: true,
          breadcrumb: true,
          permissions: [
            'merchant_index_store',
            'merchant_view_store',
            'merchant_create_store',
            'merchant_update_store',
            'merchant_activate_store',
          ],
          productTour: {
            idEn: GUIDESAIL_ID.export_list.en,
            idAr: GUIDESAIL_ID.export_list.ar,
          },
          trackEvent: TrackingEvent.global_navbar_manage_branches_clicked,
        },
      },
      {
        path: '/settings/store/detail/:storeId',
        component: () => import('@/views/settings/store/PageEditBranch.vue'),
        meta: {
          title: 'header_store_detail',
          label: 'header_store_detail',
          affix: false,
          breadcrumb: true,
          permissions: [
            'merchant_view_store',
            'merchant_update_store',
            'merchant_activate_store',
          ],
        },
        hidden: true,
      },
      {
        path: '/settings/store/new',
        component: () => import('@/views/settings/store/PageCreateBranch.vue'),
        meta: {
          title: 'header_store_create',
          label: 'header_store_create',
          affix: false,
          breadcrumb: true,
          permissions: ['merchant_create_store'],
        },
        hidden: true,
      },
      {
        path: '/settings/instore-material-delivery/list',
        component: () =>
          import('@/views/settings/instore-material/PageListing.vue'),
        meta: {
          title: 'header_instore_material_delivery',
          label: 'header_instore_material_delivery',
          affix: true,
          breadcrumb: true,
          permissions: ['instore_material_delivery_list'],
          pageEvent: TrackingEvent.page_view.instore_material_page_viewed,
          trackEvent: TrackingEvent.global_navbar_instore_material_clicked,
        },
      },
      {
        path: '/settings/instore-material-delivery/new',
        component: () =>
          import('@/views/settings/instore-material/PageCreate.vue'),
        meta: {
          title: 'header_instore_material_delivery',
          label: 'header_instore_material_delivery',
          affix: false,
          breadcrumb: true,
          permissions: ['instore_material_delivery_create'],
        },
        hidden: true,
      },
      {
        path: '/settings/barcode',
        component: () => import('@/views/settings/barcode/index.vue'),
        name: 'settings-barcode',
        meta: {
          title: 'setting_barcode_title',
          label: 'setting_barcode_title',
          icon: '',
          breadcrumb: true,
          affix: true,
          permissions: ['merchant_get_barcode_setting'],
          trackEvent: TrackingEvent.global_navbar_instore_barcode_clicked,
        },
      },
    ],
  },
  {
    path: '/users',
    component: Layout,
    name: 'users',
    redirect: '/users/list',
    meta: {
      title: 'header_users',
      label: 'header_users',
      icon: 'users',
      iconActive: 'users',
      breadcrumb: true,
      trackEvent: TrackingEvent.global_navbar_users_clicked,
    },
    children: [
      {
        path: '/users/list',
        component: () => import('@/views/users/list'),
        name: 'users-list',
        meta: {
          title: 'header_user_settings',
          label: 'header_user_settings',
          icon: '',
          affix: true,
          breadcrumb: true,
          permissions: ['view_user'],
          productTour: {
            idEn: GUIDESAIL_ID.user_list.en,
            idAr: GUIDESAIL_ID.user_list.ar,
          },
          trackEvent: TrackingEvent.global_navbar_manage_users_clicked,
        },
      },
      {
        path: '/users/detail/:userId',
        component: () => import('@/views/users/detail'),
        meta: {
          title: 'header_users_detail',
          label: 'header_users_detail',
          affix: false,
          breadcrumb: true,
          permissions: ['view_user', 'add_user', 'edit_user'],
          productTour: {
            idEn: GUIDESAIL_ID.user_list.en,
            idAr: GUIDESAIL_ID.user_list.ar,
          },
        },
        hidden: true,
      },
      {
        path: '/roles/list',
        component: () => import('@/views/roles/list'),
        name: 'roles-list',
        meta: {
          title: 'header_roles_settings',
          label: 'header_roles_settings',
          icon: '',
          affix: true,
          breadcrumb: true,
          productTour: {
            idEn: GUIDESAIL_ID.role_list.en,
            idAr: GUIDESAIL_ID.role_list.ar,
          },
          trackEvent: TrackingEvent.global_navbar_manage_roles_clicked,
        },
      },
      {
        path: '/roles/:roleId',
        component: () => import('@/views/roles/detail'),
        name: 'roles-create',
        meta: {
          title: 'header_roles_details',
          label: 'header_roles_details',
          affix: true,
          breadcrumb: true,
          permissions: ['view_roles', 'add_role', 'edit_role'],
          productTour: {
            idEn: GUIDESAIL_ID.role_detail.en,
            idAr: GUIDESAIL_ID.role_detail.ar,
          },
        },
        hidden: true,
      },
    ],
  },
  {
    path: '/v2/profile',
    component: () => {},
    name: 'business-profile',
    redirect: '/v2/profile',
    meta: {
      title: 'header_business_profile',
      label: 'header_business_profile',
      icon: 'users',
      iconActive: 'users',
      breadcrumb: false,
      useFullPath: true, // Navigate without hash (#)
      permissions: ['business_profile_view'],
    },
    hidden: () => {
      const abtProvider = getABTProvider()
      return !abtProvider.isFeatureEnabled(
        'perm_gate_merchant_business_profile'
      )
    },
    children: [
      {
        path: '/v2/profile',
        component: () => {},
        name: 'business-profile',
        meta: {
          title: 'header_business_profile',
          label: 'header_business_profile',
          icon: 'users',
          iconActive: 'users',
          affix: true,
          breadcrumb: true,
          useFullPath: true, // Navigate without hash (#)
          permissions: ['business_profile_view'],
          tag: {
            label: 'common_new',
            type: 'success',
          },
        },
      },
    ],
  },
  createUserSettingsRoute(),
  {
    path: '/kyc',
    component: Layout,
    name: 'kyc',
    redirect: '/kyc-legal',
    meta: {
      title: 'tab_kyc-onboarding',
      label: 'tab_kyc-onboarding',
      icon: 'onboarding-hand',
      iconActive: 'onboarding-hand',
      breadcrumb: false,
      isKyc: true,
      permissions: ['get_kyc', 'store_kyc', 'view_dashboard_overview'],
    },
    beforeEnter: (_, __, next) => {
      // If user is not logged in, redirect to onboarding v3
      if (!getToken()) {
        redirectToOnboardingV3()

        return
      }

      // If user is logged in, redirect user to onboarding form
      next()
    },
    children: [
      {
        path: '/kyc-business',
        component: () => import('@/views/kyc/kyc-business.vue'),
        name: 'kyc-business',
        meta: {
          title: 'tab_kyc-business',
          label: 'tab_kyc-business',
          affix: true,
          breadcrumb: true,
          isKyc: true,
          permissions: ['get_kyc', 'store_kyc', 'view_dashboard_overview'],
        },
      },
      {
        path: '/kyc-legal',
        component: () => import('@/views/kyc/kyc-legal.vue'),
        name: 'kyc-legal',
        meta: {
          title: 'tab_kyc-legal',
          label: 'tab_kyc-legal',
          affix: true,
          breadcrumb: true,
          isKyc: true,
          permissions: ['get_kyc', 'store_kyc', 'view_dashboard_overview'],
        },
      },
      {
        path: '/kyc-bank',
        component: () => import('@/views/kyc/kyc-bank.vue'),
        name: 'kyc-bank',
        meta: {
          title: 'tab_kyc-bank',
          label: 'tab_kyc-bank',
          affix: true,
          breadcrumb: true,
          isKyc: true,
          permissions: ['get_kyc', 'store_kyc', 'view_dashboard_overview'],
        },
      },
      {
        path: '/kyc-additional-info',
        component: () => import('@/views/kyc/additional-info.vue'),
        name: 'kyc-additional-info',
        meta: {
          title: 'tab-kyc-additional-info',
          label: 'tab-kyc-additional-info',
          affix: true,
          breadcrumb: true,
          isKyc: true,
          permissions: ['get_kyc', 'store_kyc', 'view_dashboard_overview'],
        },
      },
      {
        path: '/kyc-contact',
        component: () => import('@/views/contact-details/index'),
        name: 'kyc-contact',
        meta: {
          title: 'tab_kyc-contact',
          label: 'tab_kyc-contact',
          affix: true,
          breadcrumb: true,
          isKyc: true,
          hideStepNumber: true,
          permissions: ['get_merchant_onboarding_contacts'],
        },
      },
    ],
  },
]

const createRouter = () =>
  new Router({
    routes: constantRoutes,
    scrollBehavior() {
      document.getElementById('app').scrollIntoView({ behavior: 'smooth' })
    },
  })

const router = createRouter()

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher // reset router
}

// Debounce page view tracking to avoid multiple calls
const pageViewTracking = debounce((eventName, payload) => {
  locator.tracking.event(eventName, payload)
}, 1234)

// Listener after resolved
router.afterEach((to) => {
  locator.chatbot.toggleChatBotIfNeeded(to)
  locator.productTour.changeTourByRoute(to)

  if (to.meta.pageEvent) {
    pageViewTracking(to.meta.pageEvent)
  }
})

export default router
